import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { validateEmail } from '@/lib/emailValidation'
import { redirectToDashboard } from './utils/authHelpers'
import { EmailService } from '@/lib/email'
import AuthForm from './components/AuthForm'
import RoleSelection from './components/RoleSelection'
import LoadingScreen from './components/LoadingScreen'
import OTPVerification from './components/OTPVerification'
import { useAuth } from '@/context/AuthContext'
import { UserRole } from '@/types'

export default function AuthPageContent() {
  const { user, loading, login, refreshUser } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  
  // API URL utility
  const getApiUrl = () => {
    const baseUrl = import.meta.env.VITE_API_URL
    if (!baseUrl) {
      if (import.meta.env.DEV) return 'http://localhost:5001/api'
      console.error('VITE_API_URL is not set in production!')
      return ''
    }
    const cleanUrl = baseUrl.replace(/\/$/, '')
    return cleanUrl.endsWith('/api') ? cleanUrl : `${cleanUrl}/api`
  }
  const API_URL = getApiUrl()

  // Form + UI state
  const [isSignUp, setIsSignUp] = useState(true)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [formLoading, setFormLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState<'error' | 'success' | 'info'>('error')
  const [step, setStep] = useState<'auth' | 'otp' | 'role'>('auth')
  const [confirmationEmail, setConfirmationEmail] = useState('')
  const [selectedRole, setSelectedRole] = useState<UserRole | null>(null)
  const [emailValidation, setEmailValidation] = useState<{ isValid: boolean; error?: string } | null>(null)
  const [emailValidationTimer, setEmailValidationTimer] = useState<NodeJS.Timeout | null>(null)
  const [hasRedirected, setHasRedirected] = useState(false)

  // Handle URL-based events (email confirmation, logout, mode=signin|signup)
  useEffect(() => {
    const logoutMessage = searchParams.get('message')
    if (logoutMessage) setMessage(logoutMessage)

    const mode = searchParams.get('mode')
    if (mode === 'signin') setIsSignUp(false)
    else if (mode === 'signup') setIsSignUp(true)

    const emailConfirmed = searchParams.get('email_confirmed')
    const redirectTo = searchParams.get('redirect_to')

    if (emailConfirmed === 'true' && redirectTo === 'role_selection') {
      setMessage('✅ Email confirmed! Please select your role to complete registration.')
      setStep('role')
    } else if (window.location.hash.includes('access_token')) {
      setMessage('✅ Email confirmed! Please select your role to complete registration.')
      setStep('role')
    }
  }, [searchParams])

  // Auto-redirect if authenticated
  useEffect(() => {
    if (loading || hasRedirected) return

    if (user?.role) {
      redirectToDashboard(user.role, navigate)
      setHasRedirected(true)
    } else if (user && !user.role) {
      setStep('role')
      setMessage('Please select your role to complete registration.')
    }
  }, [loading, hasRedirected, user, navigate])
  
  // Auth handler for sign in/up
  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormLoading(true)
    setMessage('')

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setMessage('Please enter a valid email.')
      setFormLoading(false)
      return
    }

    // SIGN UP
    if (isSignUp) {
      if (!firstName || !lastName || !selectedRole) {
        setMessage('Please complete all fields.')
        setFormLoading(false)
        return
      }
      if (password !== confirmPassword) {
        setMessage('Passwords do not match.')
        setFormLoading(false)
        return
      }

      try {
        const response = await fetch(`${API_URL}/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            password,
            role: selectedRole,
            firstName,
            lastName,
          }),
        })

        // Handle rate limiting (429) specifically
        if (response.status === 429) {
          setMessageType('error')
          setMessage('Too many requests. Please wait a moment and try again.')
          setFormLoading(false)
          return
        }

        // Try to parse JSON, but handle non-JSON responses
        let data: any = {}
        const contentType = response.headers.get('content-type')
        if (contentType && contentType.includes('application/json')) {
          try {
            data = await response.json()
          } catch (parseError) {
            // If JSON parsing fails, try to get text
            const text = await response.text()
            console.error('Failed to parse JSON response:', text)
            setMessageType('error')
            setMessage(text || 'Sign up failed. Please try again.')
            setFormLoading(false)
            return
          }
        } else {
          // Non-JSON response (e.g., plain text error)
          const text = await response.text()
          setMessageType('error')
          setMessage(text || 'Sign up failed. Please try again.')
          setFormLoading(false)
          return
        }

        if (!response.ok) {
          setMessageType('error')
          setMessage(data.message || 'Sign up failed.')
        } else {
          const emailSent = data.verificationEmailSent !== false
          setMessageType(emailSent ? 'success' : 'info')
          setMessage(
            emailSent
              ? '✅ OTP has been sent to your email. Please verify.'
              : "⚠️ We couldn't send the verification email. You can still enter an OTP if you have one, or click \"Resend OTP\" to try again (requires server email config)."
          )
          setConfirmationEmail(email)
          setStep('otp')
        }
      } catch (err) {
        console.error(err)
        setMessageType('error')
        setMessage('Server error. Please try again.')
      } finally {
        setFormLoading(false)
      }
    }

    // LOGIN
    else {
      try {
        const response = await fetch(`${API_URL}/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password }),
        })

        // Handle rate limiting (429) specifically
        if (response.status === 429) {
          setMessageType('error')
          setMessage('Too many requests. Please wait a moment and try again.')
          setFormLoading(false)
          return
        }

        // Try to parse JSON, but handle non-JSON responses
        let data: any = {}
        const contentType = response.headers.get('content-type')
        if (contentType && contentType.includes('application/json')) {
          try {
            data = await response.json()
          } catch (parseError) {
            // If JSON parsing fails, try to get text
            const text = await response.text()
            console.error('Failed to parse JSON response:', text)
            setMessageType('error')
            setMessage(text || 'Login failed. Please try again.')
            setFormLoading(false)
            return
          }
        } else {
          // Non-JSON response (e.g., plain text error)
          const text = await response.text()
          setMessageType('error')
          setMessage(text || 'Login failed. Please try again.')
          setFormLoading(false)
          return
        }

        if (!response.ok) {
          setMessageType('error')
          setMessage(data.message || 'Login failed.')
        } else {
          setMessageType('success')
          setMessage('Login successful.')
          await login(data.token, data.user)
          // Redirect is handled by the useEffect when user?.role updates, so the
          // dashboard mounts after auth context has the latest user (including
          // is_verified from refreshUser()), fixing verification status without refresh.
        }
      } catch (err) {
        console.error(err)
        setMessageType('error')
        setMessage('Server error. Please try again.')
      } finally {
        setFormLoading(false)
      }
    }
  }

  // OTP verified handler
  const handleOTPVerified = async (token: string) => {
    void token // Mark as used to satisfy lint
    await refreshUser()
    if (selectedRole) {
      redirectToDashboard(selectedRole, navigate)
    } else {
      setStep('role')
    }
  }

  // Role selection handler
  const handleRoleSelection = async (role: UserRole) => {
    setFormLoading(true)
    setMessage('')

    try {
      if (!user) {
        setMessage('User session not found. Please sign in again.')
        return
      }

      const updatedUser = { ...user, role }
      localStorage.setItem('user', JSON.stringify(updatedUser))

      try {
        const emailData = {
          id: user.id,
          email: user.email,
          role,
          is_verified: true,
          kyc_data: { firstName, lastName },
        }
        await EmailService.sendWelcomeEmail(emailData)
      } catch (emailError) {
        console.error('Welcome email error:', emailError)
      }

      setMessage(`Role set to ${role}! Redirecting...`)
      setTimeout(() => redirectToDashboard(role, navigate), 2000)
    } catch (error) {
      console.error('Role selection error:', error)
      setMessage('Please sign in first before selecting a role.')
      setStep('auth')
    } finally {
      setFormLoading(false)
    }
  }

  // Email validation with debounce
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value
    setEmail(newEmail)

    if (!newEmail) {
      setEmailValidation(null)
      return
    }

    if (isSignUp) {
      if (emailValidationTimer) clearTimeout(emailValidationTimer)
      const timer = setTimeout(async () => {
        try {
          const validation = await validateEmail(newEmail)
          setEmailValidation(validation)
        } catch {
          setEmailValidation({ isValid: false, error: 'Unable to verify email' })
        }
      }, 500)
      setEmailValidationTimer(timer)
    }
  }

  if (loading) return <LoadingScreen />

  // ===== MAIN AUTH PAGE RENDER =====
  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding & Benefits (hidden on mobile) */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-emerald-600 to-emerald-800 p-12 flex-col justify-between overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 pattern-grid"></div>
        </div>
        
        {/* Decorative circles */}
        <div className="absolute -top-20 -left-20 w-64 h-64 bg-emerald-400/30 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-20 -right-20 w-80 h-80 bg-amber-400/20 rounded-full blur-3xl"></div>

        {/* Logo */}
        <div className="relative z-10">
          <Link to="/" className="flex items-center space-x-2">
            <img src="/logo.png" alt="LandlordNoAgent Logo" className="w-16 h-16 object-contain" />
            <span className="text-2xl font-bold text-white">
              Landlord<span className="text-emerald-200">NoAgent</span>
            </span>
          </Link>
        </div>

        {/* Main content */}
        <div className="relative z-10 my-auto">
          <h1 className="text-4xl xl:text-5xl font-display text-white mb-6 leading-tight">
            Find Your Perfect Home.
            <br />
            <span className="text-emerald-200">Save Thousands.</span>
          </h1>
          <p className="text-lg text-emerald-100 mb-10 max-w-md">
            Join thousands of smart Nigerians who skip agent fees and connect directly with landlords.
          </p>

          {/* Benefits list */}
          <div className="space-y-4">
            {[
              { icon: '💰', text: 'Save up to ₦500,000 in agent fees' },
              { icon: '✅', text: 'All properties verified for quality' },
              { icon: '🔒', text: 'Secure payments & verified users' },
              { icon: '⚡', text: 'Find your home 10x faster' },
            ].map((benefit, i) => (
              <div key={i} className="flex items-center space-x-3 text-white/90">
                <span className="text-2xl">{benefit.icon}</span>
                <span className="text-lg">{benefit.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Testimonial */}
        <div className="relative z-10 bg-white/10 backdrop-blur rounded-2xl p-6">
          <div className="flex items-start space-x-4">
            <img
              src="https://randomuser.me/api/portraits/women/44.jpg"
              alt="User"
              className="w-12 h-12 rounded-full"
            />
            <div>
              <p className="text-white/90 italic mb-2">
                "I saved ₦350,000 by renting directly. The verification process gave me confidence!"
              </p>
              <p className="text-emerald-200 font-medium">— Adaeze O., Lagos</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Auth Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-12 bg-stone-50">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex justify-center mb-8">
            <Link to="/" className="inline-block">
              <img src="/logo.png" alt="LandlordNoAgent Logo" className="w-24 h-24 object-contain" />
            </Link>
          </div>

          {/* Step 1: Auth Form */}
          {step === 'auth' && (
            <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-8">
              {/* Header */}
              <div className="text-center mb-8">
                <h2 className="text-2xl sm:text-3xl font-display text-stone-900 mb-2">
                  {isSignUp ? 'Create an Account' : 'Welcome Back'}
                </h2>
                <p className="text-stone-500">
                  {isSignUp ? 'Join the community and start saving' : 'Sign in to continue to your dashboard'}
                </p>
              </div>

              {/* Toggle Sign Up / Sign In */}
              <div className="flex mb-8 bg-stone-100 rounded-xl p-1">
                <button
                  onClick={() => {
                    setIsSignUp(true)
                    setMessage('')
                  }}
                  className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold transition-all duration-300 ${
                    isSignUp 
                      ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-500/25' 
                      : 'text-stone-600 hover:text-stone-900'
                  }`}
                >
                  Sign Up
                </button>
                <button
                  onClick={() => {
                    setIsSignUp(false)
                    setMessage('')
                  }}
                  className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold transition-all duration-300 ${
                    !isSignUp 
                      ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-500/25' 
                      : 'text-stone-600 hover:text-stone-900'
                  }`}
                >
                  Sign In
                </button>
              </div>

              <AuthForm
                isSignUp={isSignUp}
                firstName={firstName}
                lastName={lastName}
                email={email}
                password={password}
                confirmPassword={confirmPassword}
                selectedRole={selectedRole}
                loading={formLoading}
                emailValidation={emailValidation}
                onFirstNameChange={(e) => setFirstName(e.target.value)}
                onLastNameChange={(e) => setLastName(e.target.value)}
                onEmailChange={handleEmailChange}
                onPasswordChange={(e) => setPassword(e.target.value)}
                onConfirmPasswordChange={(e) => setConfirmPassword(e.target.value)}
                onRoleChange={setSelectedRole}
                onSubmit={handleAuth}
              />

              {/* Forgot Password Link */}
              {!isSignUp && (
                <div className="mt-4 text-center">
                  <button
                    type="button"
                    onClick={() => {
                      const emailParam = email ? `?email=${encodeURIComponent(email)}` : ''
                      navigate(`/auth/forgot-password${emailParam}`)
                    }}
                    className="text-sm font-medium text-emerald-600 hover:text-emerald-700 transition-colors"
                  >
                    Forgot your password?
                  </button>
                </div>
              )}

              {/* Message Display */}
              {message && (
                <div
                  className={`mt-6 p-4 rounded-xl text-sm font-medium ${
                    messageType === 'error'
                      ? 'bg-red-50 text-red-700 border border-red-200'
                      : messageType === 'success'
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      : 'bg-blue-50 text-blue-700 border border-blue-200'
                  }`}
                >
                  {message}
                </div>
              )}

              {/* Terms notice for signup */}
              {isSignUp && (
                <p className="mt-6 text-xs text-stone-400 text-center">
                  By signing up, you agree to our{' '}
                  <a href="#" className="text-emerald-600 hover:underline">Terms of Service</a>
                  {' '}and{' '}
                  <a href="#" className="text-emerald-600 hover:underline">Privacy Policy</a>
                </p>
              )}
            </div>
          )}

          {/* Step 2: OTP Verification */}
          {step === 'otp' && (
            <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-8">
              <OTPVerification 
                email={confirmationEmail} 
                onVerified={handleOTPVerified} 
                onBack={() => setStep('auth')} 
              />
            </div>
          )}

          {/* Step 3: Role Selection */}
          {step === 'role' && (
            <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-8">
              <RoleSelection 
                onSelect={handleRoleSelection} 
                loading={formLoading} 
                message={message} 
                messageType={messageType} 
              />
            </div>
          )}

          {/* Back to home link */}
          <div className="mt-6 text-center">
            <Link 
              to="/" 
              className="text-sm text-stone-500 hover:text-emerald-600 transition-colors inline-flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to home
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
